/**
 * Geographic utilities
 * Centralized location for all geo-related calculations
 */

/**
 * Convert degrees to radians
 * @param {number} deg - Degrees
 * @returns {number} Radians
 */
export const deg2rad = (deg) => deg * (Math.PI / 180);

/**
 * Convert radians to degrees
 * @param {number} rad - Radians
 * @returns {number} Degrees
 */
export const rad2deg = (rad) => rad * (180 / Math.PI);

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {number} lat1 - Latitude of point 1
 * @param {number} lon1 - Longitude of point 1
 * @param {number} lat2 - Latitude of point 2
 * @param {number} lon2 - Longitude of point 2
 * @returns {number} Distance in kilometers
 */
export const getDistanceFromLatLonInKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

/**
 * Calculate distance in meters
 * @param {number} lat1 - Latitude of point 1
 * @param {number} lon1 - Longitude of point 1
 * @param {number} lat2 - Latitude of point 2
 * @param {number} lon2 - Longitude of point 2
 * @returns {number} Distance in meters
 */
export const getDistanceInMeters = (lat1, lon1, lat2, lon2) => {
    return getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) * 1000;
};

/**
 * Check if a point is within a certain distance of another point
 * @param {number} lat1 - Latitude of point 1
 * @param {number} lon1 - Longitude of point 1
 * @param {number} lat2 - Latitude of point 2
 * @param {number} lon2 - Longitude of point 2
 * @param {number} thresholdKm - Distance threshold in kilometers
 * @returns {boolean}
 */
export const isWithinDistance = (lat1, lon1, lat2, lon2, thresholdKm) => {
    return getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) <= thresholdKm;
};

/**
 * Check if a point is within a certain distance in meters
 * @param {number} lat1 - Latitude of point 1
 * @param {number} lon1 - Longitude of point 1
 * @param {number} lat2 - Latitude of point 2
 * @param {number} lon2 - Longitude of point 2
 * @param {number} thresholdMeters - Distance threshold in meters
 * @returns {boolean}
 */
export const isWithinDistanceMeters = (lat1, lon1, lat2, lon2, thresholdMeters) => {
    return getDistanceInMeters(lat1, lon1, lat2, lon2) <= thresholdMeters;
};

/**
 * Calculate the bearing between two points
 * @param {number} lat1 - Latitude of point 1
 * @param {number} lon1 - Longitude of point 1
 * @param {number} lat2 - Latitude of point 2
 * @param {number} lon2 - Longitude of point 2
 * @returns {number} Bearing in degrees (0-360)
 */
export const getBearing = (lat1, lon1, lat2, lon2) => {
    const dLon = deg2rad(lon2 - lon1);
    const y = Math.sin(dLon) * Math.cos(deg2rad(lat2));
    const x = Math.cos(deg2rad(lat1)) * Math.sin(deg2rad(lat2)) -
              Math.sin(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.cos(dLon);
    const bearing = rad2deg(Math.atan2(y, x));
    return (bearing + 360) % 360;
};

/**
 * Get a human-readable direction from bearing
 * @param {number} bearing - Bearing in degrees
 * @returns {string} Direction (N, NE, E, SE, S, SW, W, NW)
 */
export const getDirectionFromBearing = (bearing) => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(bearing / 45) % 8;
    return directions[index];
};

/**
 * Format distance for display
 * @param {number} distanceKm - Distance in kilometers
 * @param {string} locale - Locale for formatting (default: 'nl-BE')
 * @returns {string} Formatted distance string
 */
export const formatDistance = (distanceKm, _locale = 'nl-BE') => {
    if (distanceKm < 1) {
        // Show in meters for distances less than 1km
        const meters = Math.round(distanceKm * 1000);
        return `${meters} m`;
    } else if (distanceKm < 10) {
        // Show one decimal for distances less than 10km
        return `${distanceKm.toFixed(1)} km`;
    } else {
        // Round to whole number for larger distances
        return `${Math.round(distanceKm)} km`;
    }
};

/**
 * Check if coordinates are valid
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @returns {boolean}
 */
export const isValidCoordinate = (lat, lon) => {
    return (
        typeof lat === 'number' &&
        typeof lon === 'number' &&
        !isNaN(lat) &&
        !isNaN(lon) &&
        lat >= -90 &&
        lat <= 90 &&
        lon >= -180 &&
        lon <= 180
    );
};

/**
 * Parse coordinates from various formats
 * @param {string|number} lat - Latitude
 * @param {string|number} lon - Longitude
 * @returns {{lat: number, lon: number}|null}
 */
export const parseCoordinates = (lat, lon) => {
    const parsedLat = parseFloat(lat);
    const parsedLon = parseFloat(lon);

    if (isValidCoordinate(parsedLat, parsedLon)) {
        return { lat: parsedLat, lon: parsedLon };
    }
    return null;
};

const geoUtils = {
    deg2rad,
    rad2deg,
    getDistanceFromLatLonInKm,
    getDistanceInMeters,
    isWithinDistance,
    isWithinDistanceMeters,
    getBearing,
    getDirectionFromBearing,
    formatDistance,
    isValidCoordinate,
    parseCoordinates,
};

export default geoUtils;
